import { useState } from 'react';
import { PERMISSION_LABELS, PERMISSIONS, type Permission } from '@gl/shared';

import { SleeperCaptchaRequired } from '../api/hooks.js';
import {
  useChatConnection,
  useConnectChat,
  useDisconnectChat,
  useRecalculateStats,
  useStatsJob,
  useAdminUsers,
  useCreateInvite,
  useDeleteInvite,
  useInvites,
  useSetUserPermissions,
  useSetUserSleeperId,
  useSleeperUsers,
  type SleeperUserRow,
} from '../api/hooks.js';
import { useCan } from '../auth.js';
import { EmptyState, ErrorState, Loading } from '../components/states.js';
import { Captcha } from '../components/Captcha.js';
import { Modal } from '../components/Modal.js';
import { formatDateTime, relativeTime } from '../util/format.js';

/**
 * How a Sleeper member reads in a picker.
 *
 * The id is shown alongside the name because the id is what gets stored — a
 * display name is whatever the manager set it to this week, and one in this
 * league has already changed.
 */
function sleeperLabel(row: SleeperUserRow): string {
  const team = row.teamName?.trim();
  const name = team ? `${team} (${row.displayName})` : row.displayName;
  return `${name} — ${row.sleeperUserId}`;
}

function InviteModal({ onClose }: { onClose: () => void }) {
  const [email, setEmail] = useState('');
  const [sleeperUserId, setSleeperUserId] = useState('');
  const [permissions, setPermissions] = useState<Permission[]>([]);

  const sleeperUsers = useSleeperUsers(true);
  const create = useCreateInvite();

  return (
    <Modal
      open
      title="Invite a member"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!email.trim() || !sleeperUserId || create.isPending}
            onClick={() =>
              create.mutate(
                { email: email.trim().toLowerCase(), sleeperUserId, permissions },
                { onSuccess: onClose },
              )
            }
          >
            {create.isPending ? 'Creating…' : 'Create invite'}
          </button>
        </>
      }
    >
      <div className="space-y-4">
        <div>
          <label className="label" htmlFor="invite-email">
            Google account email
          </label>
          <input
            id="invite-email"
            type="email"
            className="input"
            value={email}
            onChange={(event) => setEmail(event.target.value)}
            placeholder="someone@gmail.com"
          />
          <p className="mt-1 text-xs text-field-500">
            They can only sign in with this exact Google address.
          </p>
        </div>

        <div>
          <label className="label" htmlFor="invite-sleeper">
            Sleeper player
          </label>
          {sleeperUsers.data?.length === 0 ? (
            <p className="text-sm text-field-400">
              No Sleeper members have been imported yet — run a league sync first.
            </p>
          ) : (
            <select
              id="invite-sleeper"
              className="input"
              value={sleeperUserId}
              onChange={(event) => setSleeperUserId(event.target.value)}
            >
              <option value="">Select a Sleeper account…</option>
              {sleeperUsers.data?.map((row) => (
                <option key={`${row.season}:${row.sleeperUserId}`} value={row.sleeperUserId}>
                  {sleeperLabel(row)}
                </option>
              ))}
            </select>
          )}
        </div>

        <div>
          <span className="label">Extra permissions</span>
          <div className="space-y-1.5">
            {PERMISSIONS.map((permission) => (
              <label key={permission} className="flex items-center gap-2 text-sm text-field-200">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-brass-500"
                  checked={permissions.includes(permission)}
                  onChange={(event) =>
                    setPermissions((current) =>
                      event.target.checked
                        ? [...current, permission]
                        : current.filter((value) => value !== permission),
                    )
                  }
                />
                {PERMISSION_LABELS[permission]}
              </label>
            ))}
          </div>
          <p className="mt-2 text-xs text-field-500">
            Every member also gets: see important dates, vote in polls, suggest rule changes.
          </p>
        </div>

        {create.error ? (
          <p className="text-sm text-signal">{(create.error as Error).message}</p>
        ) : null}
      </div>
    </Modal>
  );
}

const STATUS_STYLES: Record<string, string> = {
  running: 'bg-sky-900/50 text-sky-200',
  succeeded: 'bg-emerald-900/50 text-emerald-200',
  failed: 'bg-red-950/60 text-red-200',
};

/**
 * Forces the nightly job to run now.
 *
 * The request only starts the job — a full rebuild outlasts any request — so
 * the outcome comes from polling, and a failure surfaces here because it is
 * recorded on the run rather than returned to the caller.
 */
function StatsPanel() {
  const isAdmin = useCan('admin');
  const { data, isPending, error } = useStatsJob(isAdmin);
  const recalculate = useRecalculateStats();
  const [showLog, setShowLog] = useState(false);

  const running = data?.running ?? false;
  const last = data?.lastRun ?? null;

  return (
    <div className="card">
      {isPending ? <Loading /> : null}
      {error ? <ErrorState error={error} /> : null}

      {data ? (
        <>
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="min-w-0">
              <p className="text-sm text-field-200">
                Re-runs the Sleeper ingestion, refreshes the rulebook from the league settings, and
                recomputes every statistic.
              </p>
              <p className="mt-1 text-xs text-field-500">
                {running
                  ? 'A run is in progress.'
                  : data.nextScheduledRun
                    ? `Next scheduled run ${formatDateTime(data.nextScheduledRun)}.`
                    : 'The nightly scheduler is switched off.'}
              </p>
            </div>

            <div className="flex shrink-0 gap-2">
              <button
                type="button"
                className="btn-secondary"
                disabled={running || recalculate.isPending}
                onClick={() => recalculate.mutate(false)}
                title="Only fetches what can still have changed"
              >
                Update
              </button>
              <button
                type="button"
                className="btn-primary"
                disabled={running || recalculate.isPending}
                onClick={() => {
                  if (confirm('Re-fetch every week from Sleeper and recompute everything?')) {
                    recalculate.mutate(true);
                  }
                }}
                title="Ignores which weeks are already final and rebuilds from scratch"
              >
                {running ? 'Running…' : 'Full rebuild'}
              </button>
            </div>
          </div>

          {recalculate.error ? (
            <p className="mt-3 text-sm text-signal">{(recalculate.error as Error).message}</p>
          ) : null}

          {last ? (
            <div className="mt-4 border-t border-field-800 pt-4">
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                <span className={`badge ${STATUS_STYLES[last.status] ?? 'bg-field-800'}`}>
                  {last.status}
                </span>
                <span className="text-field-300">
                  {last.full ? 'Full rebuild' : 'Update'}, started by {last.trigger}
                </span>
                <span className="text-xs text-field-500">
                  {formatDateTime(last.startedAt)}
                  {last.finishedAt
                    ? ` · took ${Math.max(
                        Math.round(
                          (new Date(last.finishedAt).getTime() -
                            new Date(last.startedAt).getTime()) /
                            1000,
                        ),
                        1,
                      )}s`
                    : ''}
                </span>
              </div>

              {last.error ? (
                <p className="mt-2 rounded-md border border-red-900/60 bg-red-950/40 px-3 py-2 text-sm text-red-200">
                  {last.error}
                </p>
              ) : null}

              {last.log.length > 0 ? (
                <>
                  <button
                    type="button"
                    className="mt-2 text-xs text-brass-300 hover:text-brass-400"
                    onClick={() => setShowLog((open) => !open)}
                  >
                    {showLog ? 'Hide' : 'Show'} log ({last.log.length} lines)
                  </button>
                  {showLog ? (
                    <pre className="mt-2 max-h-64 overflow-auto rounded-md bg-field-950 p-3 text-xs leading-relaxed text-field-300">
                      {last.log.join('\n')}
                    </pre>
                  ) : null}
                </>
              ) : null}
            </div>
          ) : (
            <p className="mt-4 border-t border-field-800 pt-4 text-sm text-field-400">
              Nothing has been calculated yet.
            </p>
          )}
        </>
      ) : null}
    </div>
  );
}


/**
 * When the session in hand runs out.
 *
 * Worth its own line because the failure it warns about is silent: a session
 * expires overnight, the sync stops mirroring, and the only sign is a chat page
 * that quietly stops growing. A week out it is a note; once it has gone, or is
 * about to, it is red — unless there is a username and password to fall back on,
 * in which case the sync signs itself back in and nobody has to do anything.
 */
function ExpiryLine({ expiresAt, canSignIn }: { expiresAt: string | null; canSignIn: boolean }) {
  if (!expiresAt) return null;

  const left = new Date(expiresAt).getTime() - Date.now();
  const gone = left <= 0;
  const soon = left < 7 * 86_400_000;

  return (
    <p className={`mt-1 text-xs ${soon && !canSignIn ? 'text-signal' : 'text-field-500'}`}>
      {gone ? 'That session expired ' : 'That session expires '}
      {formatDateTime(expiresAt)} ({relativeTime(expiresAt)})
      {canSignIn
        ? ' — the nightly sync signs in again by itself when Sleeper turns it down.'
        : soon
          ? ' — sign in again below before it does, or the chat stops mirroring.'
          : '.'}
    </p>
  );
}

/**
 * Connecting the league chat.
 *
 * The sign-in happens in this browser rather than on the server, because
 * Sleeper answers a server with a captcha and a server has no honest way to
 * satisfy one. So an admin signs in here, on their own machine, and only the
 * session that produces is sent to the site. The password goes to Sleeper and
 * nowhere else — there is no endpoint on our side that would take one.
 */
function ChatPanel() {
  const isAdmin = useCan('admin');
  const { data, isPending, error } = useChatConnection(isAdmin);
  const connect = useConnectChat();
  const disconnect = useDisconnectChat();

  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  /** Set once Sleeper has asked for a challenge, which puts one on screen. */
  const [challenge, setChallenge] = useState<string | null>(null);
  const [widgetError, setWidgetError] = useState<string | null>(null);

  const submit = (captcha?: string) =>
    connect.mutate(
      { login: login.trim(), password, captcha },
      {
        onSuccess: () => {
          setPassword('');
          setChallenge(null);
        },
        onError: (error) => {
          // A challenge is not a failure to report and stop at — it is a step,
          // so it puts the widget up instead of a red line of text.
          if (error instanceof SleeperCaptchaRequired) setChallenge(error.said);
        },
      },
    );

  const SOURCES: Record<string, string> = {
    connected: 'Reading with the session connected below.',
    environment: 'Reading with the token set in the environment.',
    'sign-in': 'Signing in with the username and password in the environment.',
    none: 'Nothing to read the chat with — it is not being mirrored.',
  };

  return (
    <div className="card">
      {isPending ? <Loading /> : null}
      {error ? <ErrorState error={error} /> : null}

      {data ? (
        <>
          <p className="text-sm text-field-200">{SOURCES[data.source]}</p>

          {/* A Sleeper session is a JWT and says when it runs out, so the page
              says so too rather than leaving the chat to go quiet one night. */}
          <ExpiryLine expiresAt={data.expiresAt} canSignIn={data.canSignIn} />

          {data.source === 'connected' ? (
            <div className="mt-3 border-t border-field-800 pt-3 text-sm">
              <p className="text-field-200">
                Connected{data.connectedAs ? ` as ${data.connectedAs}` : ''}
                {data.connectedBy ? ` by ${data.connectedBy}` : ''}
                {data.connectedAt ? ` on ${formatDateTime(data.connectedAt)}` : ''}.
              </p>
              <p className="mt-1 text-xs text-field-500">
                Can read {data.readableSeasons.join(', ') || 'nothing'}
                {data.unreadableSeasons.length > 0
                  ? ` · cannot read ${data.unreadableSeasons.join(', ')}, because that account is not in those seasons\u2019 leagues`
                  : ''}
                .
              </p>
              <button
                type="button"
                className="btn-danger mt-3"
                disabled={disconnect.isPending}
                onClick={() => disconnect.mutate()}
              >
                Disconnect
              </button>
            </div>
          ) : null}

          <div className="mt-4 border-t border-field-800 pt-4">
            <p className="mb-3 text-xs text-field-500">
              Sign in as a Sleeper account that is in the league — a bot account added as a
              co-manager is the tidy way. This happens in your browser: the password is sent to
              Sleeper and never to this site.
            </p>

            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-[14rem] flex-1">
                <label className="label" htmlFor="chat-login">
                  Sleeper username, email or phone
                </label>
                <input
                  id="chat-login"
                  className="input"
                  autoComplete="off"
                  value={login}
                  onChange={(event) => setLogin(event.target.value)}
                />
              </div>
              <div className="min-w-[12rem] flex-1">
                <label className="label" htmlFor="chat-password">
                  Password
                </label>
                <input
                  id="chat-password"
                  type="password"
                  className="input"
                  autoComplete="off"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                />
              </div>
              <button
                type="button"
                className="btn-primary"
                disabled={!login.trim() || !password || connect.isPending}
                onClick={() => submit()}
              >
                {connect.isPending ? 'Connecting\u2026' : 'Sign in and connect'}
              </button>
            </div>

            {challenge ? (
              <div className="mt-4 border-l-2 border-brass-400 pl-3">
                <p className="text-sm text-field-200">
                  Sleeper wants to check you are human: “{challenge}”
                </p>
                <p className="mt-1 text-xs text-field-500">
                  Answer it below and the sign-in goes again by itself. The answer is only good for
                  a couple of minutes.
                </p>
                <Captcha
                  siteKey={data.captchaSiteKey}
                  onSolved={(token) => submit(token)}
                  onError={setWidgetError}
                />
                {widgetError ? (
                  <p className="mt-2 text-sm text-signal">
                    {widgetError} hCaptcha only runs a challenge on domains its owner has allowed,
                    so this may be something Sleeper has to permit rather than something to retry.
                  </p>
                ) : null}
              </div>
            ) : null}

            {connect.error && !(connect.error instanceof SleeperCaptchaRequired) ? (
              <p className="mt-3 text-sm text-signal">{(connect.error as Error).message}</p>
            ) : null}
          </div>
        </>
      ) : null}
    </div>
  );
}

export function AdminPage() {
  const isAdmin = useCan('admin');
  const users = useAdminUsers(isAdmin);
  const invites = useInvites(isAdmin);
  const sleeperUsers = useSleeperUsers(isAdmin);
  const setPermissions = useSetUserPermissions();
  const setSleeperId = useSetUserSleeperId();
  const deleteInvite = useDeleteInvite();
  const [inviting, setInviting] = useState(false);

  if (!isAdmin) {
    return (
      <div>
        <div className="page-head">
          <p className="eyebrow mb-1">Admin</p>
          <h1 className="page-title">Website settings</h1>
        </div>
        <EmptyState>This page is for league administrators.</EmptyState>
      </div>
    );
  }

  return (
    <div>
      <div className="page-head">
        <p className="eyebrow mb-1">Admin</p>
        <h1 className="page-title">Website settings</h1>
      </div>

      <section className="mb-10">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="section-title">Invites</h2>
          <button type="button" className="btn-primary" onClick={() => setInviting(true)}>
            Invite a member
          </button>
        </div>

        {invites.isPending ? <Loading /> : null}
        {invites.error ? <ErrorState error={invites.error} /> : null}
        {invites.data?.length === 0 ? <EmptyState>No invites yet.</EmptyState> : null}

        <div className="space-y-2">
          {invites.data?.map((invite) => (
            <div key={invite.id} className="card flex items-center justify-between gap-3 py-3">
              <div className="min-w-0">
                <p className="truncate text-sm text-field-100">{invite.email}</p>
                <p className="text-xs text-field-500">
                  {invite.sleeperDisplayName ?? invite.sleeperUserId}
                  {invite.acceptedAt
                    ? ` · accepted ${formatDateTime(invite.acceptedAt)}`
                    : ' · pending'}
                </p>
              </div>
              {!invite.acceptedAt ? (
                <button
                  type="button"
                  className="btn-danger shrink-0"
                  onClick={() => {
                    if (confirm(`Revoke the invite for ${invite.email}?`)) {
                      deleteInvite.mutate(invite.id);
                    }
                  }}
                >
                  Revoke
                </button>
              ) : (
                <span className="badge shrink-0 bg-emerald-900/50 text-emerald-200">Accepted</span>
              )}
            </div>
          ))}
        </div>
      </section>

      <section className="mb-10">
        <h2 className="section-title mb-3">Members</h2>
        {users.isPending ? <Loading /> : null}
        {users.error ? <ErrorState error={users.error} /> : null}

        <div className="space-y-3">
          {users.data?.map((member) => (
            <div key={member.id} className="card">
              <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                  {member.avatarUrl ? (
                    <img src={member.avatarUrl} alt="" className="h-8 w-8 rounded-full" />
                  ) : null}
                  <div className="min-w-0">
                    <p className="truncate text-sm text-field-100">{member.displayName}</p>
                    <p className="truncate text-xs text-field-500">{member.email}</p>
                  </div>
                </div>
                {member.disabled ? (
                  <span className="badge bg-red-950/60 text-red-200">Disabled</span>
                ) : null}
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-2">
                <label
                  className="text-xs font-medium text-field-400"
                  htmlFor={`sleeper-${member.id}`}
                >
                  Sleeper account
                </label>
                <select
                  id={`sleeper-${member.id}`}
                  className="input w-auto max-w-full py-1.5 text-sm"
                  value={member.sleeperUserId ?? ''}
                  onChange={(event) =>
                    setSleeperId.mutate({
                      id: member.id,
                      sleeperUserId: event.target.value || null,
                    })
                  }
                >
                  <option value="">Not linked</option>
                  {/*
                    An id the ingest has never seen would otherwise silently read
                    as "Not linked", which is the one thing this list must not do.
                  */}
                  {member.sleeperUserId &&
                  !sleeperUsers.data?.some((row) => row.sleeperUserId === member.sleeperUserId) ? (
                    <option value={member.sleeperUserId}>
                      Unknown Sleeper user — {member.sleeperUserId}
                    </option>
                  ) : null}
                  {sleeperUsers.data?.map((row) => (
                    <option key={`${row.season}:${row.sleeperUserId}`} value={row.sleeperUserId}>
                      {sleeperLabel(row)}
                    </option>
                  ))}
                </select>
              </div>

              <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
                {PERMISSIONS.map((permission) => (
                  <label
                    key={permission}
                    className="flex items-center gap-1.5 text-xs text-field-300"
                  >
                    <input
                      type="checkbox"
                      className="h-3.5 w-3.5 accent-brass-500"
                      checked={member.permissions.includes(permission)}
                      onChange={(event) => {
                        const next = event.target.checked
                          ? [...member.permissions, permission]
                          : member.permissions.filter((value) => value !== permission);
                        setPermissions.mutate({ id: member.id, permissions: next });
                      }}
                    />
                    {PERMISSION_LABELS[permission]}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>

        {setPermissions.error ? (
          <p className="mt-2 text-sm text-signal">{(setPermissions.error as Error).message}</p>
        ) : null}
        {setSleeperId.error ? (
          <p className="mt-2 text-sm text-signal">{(setSleeperId.error as Error).message}</p>
        ) : null}
        {sleeperUsers.data?.length === 0 ? (
          <p className="mt-2 text-xs text-field-500">
            No Sleeper members have been imported yet — run a league sync before linking accounts.
          </p>
        ) : null}
      </section>

      <section className="mb-10">
        <h2 className="section-title mb-3">Statistics</h2>
        <StatsPanel />
      </section>

      <section>
        <h2 className="section-title mb-3">League chat</h2>
        <ChatPanel />
      </section>

      {inviting ? <InviteModal onClose={() => setInviting(false)} /> : null}
    </div>
  );
}
